import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { listInfoSections, createInfoSection, reorderInfoSections } from '../../services/api';
import { FiPlus, FiX, FiArrowUp, FiArrowDown, FiEyeOff } from 'react-icons/fi';
import Toast from '../../components/Toast';

// Member-facing "website" of admin-edited folders. Members land here from the
// sidebar's Information link; admin sees the same grid plus an Add Section
// button and inline reorder arrows.
const InfoHub = ({ basePath }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', icon: '', description: '', content: '', visible: true });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = () => {
    setLoading(true);
    listInfoSections()
      .then(r => setSections(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.title.trim()) return showToast('Title is required', 'error');
    setSaving(true);
    try {
      const r = await createInfoSection(form);
      showToast('Section created');
      setAdding(false);
      setForm({ title: '', icon: '', description: '', content: '', visible: true });
      navigate(`${basePath}/${r.data.slug}`);
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const move = async (idx, dir) => {
    const next = [...sections];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setSections(next);
    try { await reorderInfoSections(next.map(s => s.slug)); }
    catch { showToast('Order save failed — refresh to undo', 'error'); load(); }
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    const d = new Date(ts.replace(' ', 'T') + 'Z');
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1>Information</h1>
          <p>Browse the church's pages, programs, and pictures</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            <FiPlus /> Add Section
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: '#9ca3af', fontSize: 13, padding: 20 }}>Loading…</div>
      ) : sections.length === 0 ? (
        <div className="card">
          <div className="empty-state" style={{ padding: '60px 24px' }}>
            <p>No sections yet.</p>
            {isAdmin && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setAdding(true)}><FiPlus /> Create the first section</button>}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {sections.map((s, i) => (
            <div key={s.slug}
              onClick={() => navigate(`${basePath}/${s.slug}`)}
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: 18,
                cursor: 'pointer',
                position: 'relative',
                transition: 'box-shadow 0.15s, transform 0.1s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                opacity: s.visible === 0 ? 0.6 : 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.10)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
            >
              {s.visible === 0 && (
                <span style={{ position: 'absolute', top: 10, right: 10, background: '#fef3c7', color: '#a16207', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiEyeOff size={11} /> Hidden
                </span>
              )}
              <div style={{ fontSize: 40, marginBottom: 10 }}>{s.icon || '📄'}</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#111827', marginBottom: 4 }}>{s.title}</div>
              {s.description && (
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.45, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {s.description}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span>{s.image_count > 0 ? `${s.image_count} photo${s.image_count === 1 ? '' : 's'}` : 'No photos'}</span>
                <span>{s.updated_at ? `Updated ${timeAgo(s.updated_at)}` : ''}</span>
              </div>
              {isAdmin && (
                <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, marginTop: 10, borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
                  <button className="btn-icon" title="Move up"   onClick={() => move(i, -1)} disabled={i === 0}                  style={{ fontSize: 13 }}><FiArrowUp /></button>
                  <button className="btn-icon" title="Move down" onClick={() => move(i,  1)} disabled={i === sections.length - 1} style={{ fontSize: 13 }}><FiArrowDown /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="modal-overlay" onClick={() => setAdding(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div><h3>New Section</h3><p>A new folder for members to browse.</p></div>
              <button className="btn-icon" onClick={() => setAdding(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Icon <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>(emoji)</span></label>
                  <input value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} placeholder="📜" maxLength={4} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Title <span className="required">*</span></label>
                  <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Rules & Regulations" />
                </div>
                <div className="form-group form-full">
                  <label>Short note <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>(shown on the card)</span></label>
                  <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Church bylaws and member guidelines" />
                </div>
                <div className="form-group form-full">
                  <label>Content <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>(you can edit later)</span></label>
                  <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={6}
                    style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6, padding: 10, border: '1px solid var(--border)', borderRadius: 8 }} />
                </div>
                <div className="form-group form-full">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.visible} onChange={e => setForm(p => ({ ...p, visible: e.target.checked }))} />
                    Visible to members
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAdding(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default InfoHub;
